import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Combobox } from "@/components/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Loader2, Check, X, Ban, Trash2, CalendarOff, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  Pending: { bg: "#FEF3C7", text: "#92400E", dot: "#D97706" },
  Approved: { bg: "#D1FAE5", text: "#065F46", dot: "#059669" },
  Rejected: { bg: "#FEE2E2", text: "#991B1B", dot: "#DC2626" },
  Cancelled: { bg: "#F1F5F9", text: "#475569", dot: "#94A3B8" },
  Expired: { bg: "#E2E8F0", text: "#64748B", dot: "#94A3B8" },
};
const empty = { name: null, support_worker: null, leave_type: "Leave", from_date: "", to_date: "", full_day: true, start_time: "09:00", end_time: "17:00", reason: "" };
const hm = (t) => { const s = String(t || ""); return s ? s.padStart(8, "0").slice(0, 5) : ""; };

export default function Leave() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ worker: null, status: params.get("status") || "" });
  const [showExpired, setShowExpired] = useState(false);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  // action = { row, type: "Rejected" | "Cancelled", reason }
  const [action, setAction] = useState(null);
  const [acting, setActing] = useState(false);

  const wOpts = useMemo(() => staff.filter((s) => s.status === "Active").map((s) => ({ value: s.name, label: s.display_name || s.name })), [staff]);

  useEffect(() => { api.staff().then(setStaff).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    api.leave({ support_worker: filters.worker || undefined, status: filters.status || undefined, include_expired: showExpired || undefined })
      .then(setRows).catch(() => toast.error("Failed to load leave")).finally(() => setLoading(false));
  };
  useEffect(load, [filters, showExpired]); // eslint-disable-line

  const setStatusFilter = (v) => {
    setFilters((f) => ({ ...f, status: v }));
    const next = new URLSearchParams(params);
    if (v) next.set("status", v); else next.delete("status");
    setParams(next, { replace: true });
  };

  const openNew = () => setEdit({ ...empty });
  const openEdit = (r) => setEdit({
    name: r.name, support_worker: r.support_worker, leave_type: r.leave_type,
    from_date: r.from_date, to_date: r.to_date, full_day: !!r.full_day,
    start_time: hm(r.start_time) || "09:00", end_time: hm(r.end_time) || "17:00",
    reason: r.reason || "",
  });

  const save = async () => {
    const e = edit;
    if (!e.support_worker) return toast.error("Choose a worker");
    if (!e.from_date || !e.to_date) return toast.error("Set the leave dates");
    setSaving(true);
    const body = { support_worker: e.support_worker, leave_type: e.leave_type, from_date: e.from_date,
      to_date: e.to_date, full_day: e.full_day, start_time: e.full_day ? null : e.start_time + ":00",
      end_time: e.full_day ? null : e.end_time + ":00", reason: e.reason };
    try {
      let res;
      if (e.name) res = await api.updateLeave(e.name, body);
      else res = await api.createLeave(body);
      const n = res?.auto_unassigned?.length || 0;
      toast.success(e.name ? "Leave updated" : "Leave added — approved");
      if (n > 0) toast.info(`${n} published shift${n === 1 ? "" : "s"} unassigned — now in the unassigned queue`);
      setEdit(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save leave"); }
    finally { setSaving(false); }
  };

  const approve = async (r) => {
    try {
      const res = await api.setLeaveStatus(r.name, "Approved");
      toast.success("Leave approved");
      const n = res?.auto_unassigned?.length || 0;
      if (n > 0) toast.info(`${n} published shift${n === 1 ? "" : "s"} unassigned — now in the unassigned queue`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const runAction = async () => {
    const reason = (action.reason || "").trim();
    if (!reason) return toast.error(action.type === "Rejected" ? "A reason is required to reject leave." : "A reason is required to cancel approved leave.");
    setActing(true);
    try {
      await api.setLeaveStatus(action.row.name, action.type, reason);
      toast.success(action.type === "Rejected" ? "Leave rejected" : "Leave cancelled");
      setAction(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActing(false); }
  };

  const remove = async (r) => {
    try { await api.deleteLeave(r.name); toast.success("Leave deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4">
      <div className="bg-white border border-slate-200 rounded-lg mb-3">
        <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <CalendarOff className="h-4 w-4 text-slate-500" />
            <div>
              <div className="text-sm font-bold text-slate-900">Worker Leave</div>
              <div className="text-[10px] text-slate-400">Personal &amp; general leave. Office-entered leave is granted immediately; approved leave warns the roster and is skipped by template-apply.</div>
            </div>
          </div>
          {can.has("leave.edit") && (
            <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: "#bf3b4f" }} onClick={openNew} data-testid="add-leave-button">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Leave
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 p-2.5">
          <div className="w-52"><Combobox testid="leave-filter-worker" options={wOpts} value={filters.worker} onChange={(v) => setFilters((f) => ({ ...f, worker: v }))} placeholder="All workers" /></div>
          <select data-testid="leave-filter-status" value={filters.status} onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 text-slate-700">
            <option value="">All statuses</option>
            {["Pending", "Approved", "Rejected", "Cancelled", "Expired"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none ml-1" data-testid="leave-show-expired">
            <input type="checkbox" className="accent-[#bf3b4f]" checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} />
            Show expired
          </label>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-sm text-slate-400" data-testid="leave-empty">No leave records.</div>
        ) : (
          <table className="w-full text-xs" data-testid="leave-table">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-400">
              <th className="px-3 py-2">Worker</th><th className="px-2 py-2">Type</th><th className="px-2 py-2">Dates</th>
              <th className="px-2 py-2">When</th><th className="px-2 py-2">Status</th><th className="px-2 py-2">Reason</th><th className="px-3 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.Pending;
                return (
                  <tr key={r.name} className="border-t border-slate-100" data-testid={`leave-row-${r.name}`}>
                    <td className="px-3 py-2 font-medium text-slate-700">{r.support_worker_name || r.support_worker}</td>
                    <td className="px-2 py-2 text-slate-600">{r.leave_type}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-600">{dayjs(r.from_date).format("D MMM")}{r.to_date !== r.from_date ? ` – ${dayjs(r.to_date).format("D MMM")}` : ""}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-slate-600">{r.full_day ? "All day" : `${hm(r.start_time)}–${hm(r.end_time)}`}</td>
                    <td className="px-2 py-2"><span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold" style={{ backgroundColor: st.bg, color: st.text }}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: st.dot }} />{r.status}</span></td>
                    <td className="px-2 py-2 text-slate-500 max-w-[180px] truncate" title={r.cancel_reason || r.reason || ""}>{r.cancel_reason || r.reason || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {can.has("leave.approve") && r.status === "Pending" && <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-teal-700 border-teal-200 hover:bg-teal-50" onClick={() => approve(r)} data-testid={`leave-approve-${r.name}`}><Check className="h-3 w-3 mr-0.5" />Approve</Button>}
                        {can.has("leave.approve") && r.status === "Pending" && <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-red-600 border-red-200 hover:bg-red-50" onClick={() => setAction({ row: r, type: "Rejected", reason: "" })} data-testid={`leave-reject-${r.name}`}><X className="h-3 w-3 mr-0.5" />Reject</Button>}
                        {can.has("leave.approve") && r.status === "Approved" && <Button size="sm" variant="outline" className="h-7 text-[10px] px-2 text-slate-600" onClick={() => setAction({ row: r, type: "Cancelled", reason: "" })} data-testid={`leave-cancel-${r.name}`}><Ban className="h-3 w-3 mr-0.5" />Cancel</Button>}
                        {can.has("leave.edit") && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(r)} data-testid={`leave-edit-${r.name}`}><Pencil className="h-3 w-3" /></Button>}
                        {can.has("leave.delete") && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => remove(r)} data-testid={`leave-delete-${r.name}`}><Trash2 className="h-3 w-3" /></Button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / edit leave */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="leave-dialog">
          <DialogHeader><DialogTitle className="text-base">{edit?.name ? "Edit leave" : "Add leave"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-medium text-slate-600">Support worker</label>
                <div className="mt-1"><Combobox testid="leave-worker" options={wOpts} value={edit.support_worker} onChange={(v) => setEdit((e) => ({ ...e, support_worker: v }))} placeholder="Select worker" /></div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-600">Leave type</label>
                <select value={edit.leave_type} onChange={(e) => setEdit((s) => ({ ...s, leave_type: e.target.value }))} className="h-9 text-xs rounded-md border border-slate-200 bg-white px-2 w-full mt-1" data-testid="leave-type">
                  <option value="Leave">Leave</option>
                  <option value="Personal">Personal</option>
                </select>
                {!edit.name && <p className="text-[10px] text-slate-400 mt-1">Office-entered leave is granted immediately (no approval needed).</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[11px] font-medium text-slate-600">From date</label><Input type="date" value={edit.from_date} onChange={(e) => setEdit((s) => ({ ...s, from_date: e.target.value }))} className="h-9 text-xs mt-1" data-testid="leave-from" /></div>
                <div><label className="text-[11px] font-medium text-slate-600">To date</label><Input type="date" value={edit.to_date} min={edit.from_date} onChange={(e) => setEdit((s) => ({ ...s, to_date: e.target.value }))} className="h-9 text-xs mt-1" data-testid="leave-to" /></div>
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <span className="text-xs font-medium text-slate-700">Full day</span>
                <Switch checked={edit.full_day} onCheckedChange={(v) => setEdit((s) => ({ ...s, full_day: v }))} data-testid="leave-fullday" />
              </div>
              {!edit.full_day && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-[11px] font-medium text-slate-600">Unavailable from</label><Input type="time" value={edit.start_time} onChange={(e) => setEdit((s) => ({ ...s, start_time: e.target.value }))} className="h-9 text-xs mt-1" data-testid="leave-start" /></div>
                  <div><label className="text-[11px] font-medium text-slate-600">Unavailable to</label><Input type="time" value={edit.end_time} onChange={(e) => setEdit((s) => ({ ...s, end_time: e.target.value }))} className="h-9 text-xs mt-1" data-testid="leave-end" /></div>
                </div>
              )}
              {!edit.full_day && <p className="text-[10px] text-slate-400 -mt-1">For split unavailability in a day (e.g. 00:00–07:00 and 19:00–00:00), add a separate leave entry for each window.</p>}
              <div><label className="text-[11px] font-medium text-slate-600">Reason (optional)</label><Textarea value={edit.reason} onChange={(e) => setEdit((s) => ({ ...s, reason: e.target.value }))} className="text-xs mt-1 min-h-[60px]" data-testid="leave-reason" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEdit(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={save} disabled={saving} data-testid="leave-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save leave"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject / cancel reason */}
      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent data-testid="leave-reason-dialog">
          <DialogHeader><DialogTitle className="text-base">{action?.type === "Rejected" ? "Reject leave" : "Cancel approved leave"}</DialogTitle></DialogHeader>
          {action && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                {action.type === "Rejected"
                  ? `Reject the leave request from ${action.row.support_worker_name || action.row.support_worker}. Please give a reason — the worker will see it.`
                  : `Cancel the approved leave for ${action.row.support_worker_name || action.row.support_worker}. Please give a reason.`}
              </p>
              <Textarea autoFocus value={action.reason} onChange={(e) => setAction((a) => ({ ...a, reason: e.target.value }))}
                placeholder="Reason (required)" className="text-xs min-h-[80px]" data-testid="leave-action-reason" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAction(null)}>Back</Button>
            <Button size="sm" className={action?.type === "Rejected" ? "bg-red-600 hover:bg-red-700" : "bg-slate-700 hover:bg-slate-800"}
              onClick={runAction} disabled={acting} data-testid="leave-action-confirm">
              {acting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (action?.type === "Rejected" ? "Reject leave" : "Cancel leave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
